import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Icon } from "@lynx/ui-components";
import { Tooltip } from "react-tooltip";
import cn from "classnames";

import { payoutThunks } from "actions-thunks/payout-thunks";
import { CREATE_PAYOUT_ERROR, TO_BANK_ACCOUNT_DETAILS } from "reducers/types";
import { utilityFunctions } from "../utilities/utilityFunctions";
import BankAccountCard from "./components/BankAccountCard";
import { formatWireAmount, parseAmountInput } from "./wireUtils";

import "./payout-modals.scss";

const STEPS = { request: "request", confirm: "confirm", created: "created" };

const AVAILABLE_TOOLTIP =
  "Available for payout may be lower than your balance. It can exclude funds reserved for upcoming fees and other obligations.";

/**
 * Manual pay-out: request (amount) -> confirm (review) -> created.
 * The trader's single bank account is used implicitly.
 */
const PayoutRequestModal = ({ onClose, onSuccess, onViewTransaction }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentAccount, paymentMethods, payoutSettings } = useSelector(
    (state) => state.accounts
  );
  const { loading } = useSelector((state) => state.utils);
  const { errorMessage } = useSelector((state) => state.errors);
  const serverError = errorMessage?.find((i) => i.type === CREATE_PAYOUT_ERROR)?.message || "";

  const bankAccount = paymentMethods?.bankAccount || null;
  const available = payoutSettings?.availableForPayout;

  const [step, setStep] = useState(STEPS.request);
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [createdWire, setCreatedWire] = useState(null);

  const numericAmount = parseAmountInput(amount);

  const validate = (value) => {
    const numeric = parseAmountInput(value);
    if (!value || String(value).trim() === "") return "Please enter an amount";
    if (isNaN(numeric) || numeric <= 0) return "Please enter a valid amount";
    if (typeof available === "number" && numeric > available) {
      return "The requested amount is greater than your available payout amount.";
    }
    return "";
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    setAmountError(value.trim() ? validate(value) : "");
  };

  const handleAmountBlur = () => {
    if (!isNaN(numericAmount) && numericAmount > 0) {
      setAmount(utilityFunctions.currencyFormatter().format(numericAmount));
    }
  };

  const useFullAmount = () => {
    if (typeof available !== "number") return;
    setAmount(utilityFunctions.currencyFormatter().format(available));
    setAmountError("");
  };

  const goToBankDetails = () => {
    onClose();
    navigate(TO_BANK_ACCOUNT_DETAILS);
  };

  const handleReview = () => {
    const error = validate(amount);
    setAmountError(error);
    if (error || !bankAccount) return;
    setStep(STEPS.confirm);
  };

  const handleConfirm = async () => {
    try {
      const response = await dispatch(
        payoutThunks.createPayoutTC(currentAccount._id, {
          amount: numericAmount,
          payoutSettingsID: bankAccount._id,
        })
      );
      setCreatedWire(response?.data?.wire || null);
      setStep(STEPS.created);
      if (onSuccess) onSuccess();
    } catch (error) {
      // the server message is shown in the modal
      setStep(STEPS.request);
    }
  };

  const handleViewTransaction = () => {
    if (createdWire && onViewTransaction) {
      onViewTransaction(createdWire);
    }
    onClose();
  };

  const bankAction = bankAccount
    ? { label: "Change bank details", onClick: goToBankDetails }
    : { label: "Set bank details", onClick: goToBankDetails };

  const reviewDisabled = loading || !amount || !!amountError || !bankAccount;

  const titles = {
    [STEPS.request]: "Request Pay-Out",
    [STEPS.confirm]: "Confirm payout",
    [STEPS.created]: "Payout requested",
  };

  return (
    <div className="modal-window">
      <Modal
        className={cn("payout-modal payout-request-modal cabinet-modals", `step-${step}`)}
        overlayClassName="request-overlay"
        styleType="zimtra-primary"
        isOpen={true}
        hideBlur
        hasCloseIcon={false}
        onClickOutside={onClose}
      >
        <Modal.Header
          className="modal-inner-header"
          onClose={onClose}
          title={<h2 className="zimtra-h2">{titles[step]}</h2>}
        />

        {step === STEPS.request && (
            <Modal.Body className="modal-inner-body">
              <div className="payout-row">
                <div className="payout-label">Amount</div>
                <div className={cn("payout-field", { "error-field": amountError })}>
                  <input
                    id="payout-amount"
                    type="text"
                    className="payout-input"
                    value={amount}
                    placeholder="$0.00"
                    onChange={handleAmountChange}
                    onBlur={handleAmountBlur}
                  />
                  {typeof available === "number" && (
                    <div className="payout-hint">
                      Available for payout:{" "}
                      <button
                        type="button"
                        className="link-button"
                        id="payout-available-link"
                        onClick={useFullAmount}
                        title="Use the full available amount"
                      >
                        {formatWireAmount(available)}
                      </button>
                      <Icon name="info" className="payout-info-icon" id="payout-available-info" />
                      <Tooltip
                        anchorSelect="#payout-available-info"
                        className="payout-tooltip"
                        delayShow={150}
                      >
                        {AVAILABLE_TOOLTIP}
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>

              <BankAccountCard bank={bankAccount} action={bankAction} />

              {amountError && <div className="payout-error-text">{amountError}</div>}
              {serverError && <div className="payout-server-error">{serverError}</div>}
            </Modal.Body>
        )}
        {step === STEPS.request && (
            <Modal.Footer className="modal-inner-footer d-flex justify-content-center">
              <Button
                size="md"
                id="payout-review-btn"
                buttonStyle="button-variant-second"
                buttonClassName="cabinet-primary-btn"
                disabled={reviewDisabled}
                onClick={handleReview}
                label="Review payout"
              />
              <Button
                size="md"
                id="payout-close-btn"
                buttonStyle="button-variant-fourth"
                buttonClassName="cabinet-secondary-btn"
                onClick={onClose}
                label="Close"
              />
            </Modal.Footer>
        )}

        {step === STEPS.confirm && (
            <Modal.Body className="modal-inner-body">
              <p className="payout-text payout-text-strong">
                Payout amount: {formatWireAmount(numericAmount)}
              </p>
              <BankAccountCard bank={bankAccount} action={bankAction} />
              {serverError && <div className="payout-server-error">{serverError}</div>}
            </Modal.Body>
        )}
        {step === STEPS.confirm && (
            <Modal.Footer className="modal-inner-footer d-flex justify-content-center">
              <Button
                size="md"
                id="payout-confirm-btn"
                buttonStyle="button-variant-second"
                buttonClassName="cabinet-primary-btn"
                disabled={loading}
                onClick={handleConfirm}
                label="Confirm payout"
              />
              <Button
                size="md"
                id="payout-back-btn"
                buttonStyle="button-variant-fourth"
                buttonClassName="cabinet-secondary-btn"
                onClick={() => setStep(STEPS.request)}
                label="Back"
              />
            </Modal.Footer>
        )}

        {step === STEPS.created && (
            <Modal.Body className="modal-inner-body">
              <p className="payout-text payout-text-strong">Your payout request has been created.</p>
              <hr className="payout-divider" />
              <p className="payout-text payout-text-muted">
                Pay-out (manual) · {formatWireAmount(numericAmount)}
                <br />
                Status: Pending
              </p>
              <BankAccountCard bank={bankAccount} />
            </Modal.Body>
        )}
        {step === STEPS.created && (
            <Modal.Footer className="modal-inner-footer d-flex justify-content-center">
              <Button
                size="md"
                id="payout-view-transaction-btn"
                buttonStyle="button-variant-second"
                buttonClassName="cabinet-primary-btn"
                disabled={!createdWire}
                onClick={handleViewTransaction}
                label="View transaction"
              />
              <Button
                size="md"
                id="payout-created-close-btn"
                buttonStyle="button-variant-fourth"
                buttonClassName="cabinet-secondary-btn"
                onClick={onClose}
                label="Close"
              />
            </Modal.Footer>
        )}
      </Modal>
    </div>
  );
};

export default PayoutRequestModal;
